import {Injectable} from '@angular/core';
import {ClassroomMessage, DataService, MessageRecord, RecordArgs, RecordStack} from '@app/services/data.service';
import {Rect} from '@app/modules/svg/model/Rect';
import {ForeignObjectView} from '@app/modules/svg/element/foreign/ForeignObjectView';
import {AccessType} from '@app/modules/shared/data/constants';
import {GeogebraApplet, ViewPort} from '@app/js-lib/geogebra/GeogebraApplet';
import {Size} from '@app/modules/svg/model/Size';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {Store} from '@ngxs/store';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {Whiteboard, WhiteboardState} from '@app/state/whiteboard.state';
import {MessageHandler} from '@app/services/message-handler';
import {Observable, Subscriber} from 'rxjs';
import {WebSocketService} from '@app/modules/shared/services/web-socket.service';
import {ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {document} from 'ngx-bootstrap/utils';
import {WhiteboardEvents} from '@app/models/commands/whiteboard-commands';
import {SVGEvent} from '@app/modules/svg/dataSource/constant/SVGEvent';
import {ElementView} from '@app/modules/svg/element/ElementView';
import {GeogebraStateUpdaterService} from '@app/modules/whiteboard/services/geogebra-state-updater.service';
import {HttpClient} from '@angular/common/http';
import {AppletURL, AssetURL} from '@app/models/webportal/Asset';
import {PrivateWhiteboardService} from '@app/modules/whiteboard/services/private-whiteboard.service';
import {PrivateWhiteboard, PrivateWhiteboardState} from '@app/state/private-whiteboard.state';
import {SVGCallback} from '@app/modules/svg/Container';

@Injectable({
  providedIn: 'root'
})


export class GeoGebraService implements MessageHandler {
  private localUser: LocalUser;
  private _whiteboard: Whiteboard;
  private _privateWhiteboard: PrivateWhiteboard;
  private classroom: ClassroomStateModel;
  private dataService: DataService;

  private onResizeMouseDown: SVGCallback = this.resizeMouseDownCallBack.bind(this);
  private onResizeMouseMove: SVGCallback = this.resizeMouseMoveCallBack.bind(this);
  private onElementsResized: SVGCallback = this.elementsResizedCallback.bind(this);

  public handleRecord: (record: MessageRecord, nonQueue: boolean) => void | Promise<void>; /* should be set in GeoGebraCommands */
  public handleRecordStack: (recordStack: RecordStack) => Promise<void>; /* should be set in GeoGebraCommands */

  private readonly teacherBroadcast = '/app/whiteboard/t';

  private static readonly views = {
    is3D: 0,
    AV: 1,
    SV: 0,
    CV: 0,
    EV2: 0,
    CP: 0,
    PC: 0,
    DA: 0,
    FI: 0,
    macro: 0
  };

  private viewPropertiesOnResizeStart: ViewPort;

  constructor(
    private whiteboardService: WhiteboardService,
    private privateWhiteboardService: PrivateWhiteboardService,
    private webSocketService: WebSocketService,
    private geogebraStateUpdaterService: GeogebraStateUpdaterService,
    private http: HttpClient,
    private store: Store
  ) {
    this.store.select(LocalUserState).subscribe((localUser: LocalUser): void => {
      this.localUser = localUser;
    });

    this.store.select(WhiteboardState).subscribe((whiteboard: Whiteboard): void => {
      this._whiteboard = whiteboard;
      if (!whiteboard.container) {
        return;
      }

      this._whiteboard.container.addCallBack(SVGEvent.RESIZE_MOUSE_DOWN, this.onResizeMouseDown);
      this._whiteboard.container.addCallBack(SVGEvent.RESIZE_MOUSE_MOVE, this.onResizeMouseMove);
      this._whiteboard.container.addCallBack(SVGEvent.ELEMENTS_RESIZED, this.onElementsResized);
    });

    this.store.select(PrivateWhiteboardState).subscribe((privateWhiteboard: PrivateWhiteboard): void => {
      this._privateWhiteboard = privateWhiteboard;
      if (!privateWhiteboard.container) {
        return;
      }

      this._privateWhiteboard.container.addCallBack(SVGEvent.RESIZE_MOUSE_DOWN, this.onResizeMouseDown);
      this._privateWhiteboard.container.addCallBack(SVGEvent.RESIZE_MOUSE_MOVE, this.onResizeMouseMove);
      this._privateWhiteboard.container.addCallBack(SVGEvent.ELEMENTS_RESIZED, this.onElementsResized);
    });

    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel): void => {
      this.classroom = classroom;
    });
  }

  public resizeMouseDownCallBack(parameters): void {
    const whiteboard: Whiteboard = this.getWhiteboard();
    const [resizableElement] = whiteboard.container.focused.children;
    const appletId: string | null = resizableElement.SVG.getAttribute('data-applet-id');
    if (appletId) {
      this.viewPropertiesOnResizeStart = this.geogebraStateUpdaterService.applets[appletId].getViewProperties();

      const whiteboardService: WhiteboardService = this.getWhiteboardService();
      whiteboardService.sendResizeMouseDown(parameters);
    }
  }
  public resizeMouseMoveCallBack(parameters): void {
    const whiteboard: Whiteboard = this.getWhiteboard();
    const [resizableElement] = whiteboard.container.focused.children;
    const appletId: string | null = resizableElement.SVG.getAttribute('data-applet-id');

    if (appletId) {
      this.zoomAppletToFit(appletId, this.viewPropertiesOnResizeStart, whiteboard.container.focused.getRect(), whiteboard.streaming);

      const whiteboardService: WhiteboardService = this.getWhiteboardService();
      whiteboardService.sendResizeMouseMove(parameters);
    }
  }
  public elementsResizedCallback(parameters): void {
    const whiteboard: Whiteboard = this.getWhiteboard();
    const [resizableElement] = whiteboard.container.focused.children;
    const appletId: string | null = resizableElement.SVG.getAttribute('data-applet-id');

    if (appletId) {
      this.zoomAppletToFit(appletId, this.viewPropertiesOnResizeStart, whiteboard.container.focused.getRect(), true);

      const whiteboardService: WhiteboardService = this.getWhiteboardService();
      whiteboardService.sendElementsResized(parameters);
    }
  }

  public sendPostGGBApplet(parameters: any): void {
    const data = JSON.stringify(parameters);
    this.send(this.teacherBroadcast, {}, {
      store: true,
      broadcast: true,
      selfProcess: true,
      record: this.buildRecord(
        WhiteboardEvents.POST_GGB_APPLET,
        new RecordArgs(data)
      )
    });
  }
  public onPostGGBApplet(record: MessageRecord): Promise<any> {
    const recordData = JSON.parse(record.data);

    return this.postGGBApplet(
      recordData.rect,
      recordData.appletId,
      recordData.ggbFilePath,
      record.category,
      recordData.elementOwnerId,
      recordData.elementIndex
    );
  }

  /* sender method is in GeogebraStateUpdaterService */
  public onEditGGBApplet(record: MessageRecord): void {
    const recordData: {appletId: number; xml: string} = JSON.parse(record.data);

    const applet: GeogebraApplet = this.geogebraStateUpdaterService.applets[recordData.appletId];
    if (applet) {
      applet.setXML(recordData.xml);
    }
  }

  public sendGGBAddEvent(appletId: number, label: string): void {
    const recordData: string = JSON.stringify({appletId, label});
    this.send(this.teacherBroadcast, {}, {
      store: true,
      broadcast: true,
      selfProcess: false,
      record: this.buildRecord(
        WhiteboardEvents.GGB_ADD_EVENT,
        new RecordArgs(recordData)
      )
    });
  }
  public onGGBAddEvent(record: MessageRecord): void {
    const recordData: {appletId: number; label: string} = JSON.parse(record.data);
    const applet: GeogebraApplet = this.geogebraStateUpdaterService.applets[recordData.appletId];
    const label: string = recordData.label;

    const xml: string | undefined = applet.getXMLByLabel(label);
    const definition: string = applet.getCommandStringByLabel(label);

    if (label && definition) {
      applet.evalCommand(label + ':' + definition);
    }

    applet.evalXML(xml);
    applet.evalCommand('UpdateConstruction()');
  }

  public sendGGBResize(appletId: string, xMin: number, xMax: number, yMin: number, yMax: number, size: Size): void {
    const recordData: string = JSON.stringify({appletId, xMin, xMax, yMin, yMax, size});
    this.send(this.teacherBroadcast, {}, {
      store: true,
      broadcast: true,
      selfProcess: false,
      record: this.buildRecord(
        WhiteboardEvents.GGB_RESIZE,
        new RecordArgs(recordData)
      )
    });
  }
  public onGGBResize(record: MessageRecord): void {
    const recordData: {appletId: number; xMin: number; xMax: number; yMin: number; yMax: number; size: Size} = JSON.parse(record.data);
    const applet: GeogebraApplet = this.geogebraStateUpdaterService.applets[recordData.appletId];

    applet.setWidth(recordData.size.width);
    applet.setHeight(recordData.size.height);
    applet.setCoordSystem(recordData.xMin, recordData.xMax, recordData.yMin, recordData.yMax);
  }

  public addGGBApplet(appletURL: AppletURL): void {
    const LEFT_MARGIN: number = 10;
    const ASSET_TOP_MARGIN: number = 10;

    const whiteboardService: WhiteboardService = this.getWhiteboardService();
    const whiteboard: Whiteboard = this.getWhiteboard();

    const postPositionTop: number = whiteboardService.calculatePostPosition();

    let postPositionBottom: number = postPositionTop;

    postPositionBottom += ASSET_TOP_MARGIN;

    this.sendPostGGBApplet({
      elementOwnerId: this.localUser.id + '',
      elementIndex: whiteboard.container.nextElementIndex,
      rect: {x: LEFT_MARGIN, y: postPositionBottom, width: appletURL.width, height: appletURL.height},
      appletId: appletURL.id,
      ggbFilePath: appletURL.URL
    });

    postPositionBottom += appletURL.height;

    whiteboardService.normalizeWBScroll(postPositionTop, postPositionBottom, appletURL.height);
  }

  public static assetURLsToAppletURLs(assetURLs: AssetURL[]): Promise<AppletURL[]> {
    const appletURLPromises: Promise<AppletURL>[] = assetURLs.map((assetURL: AssetURL): Promise<AppletURL> =>
      GeoGebraService.assetURLToAppletURL(assetURL)
    );

    return Promise.all(appletURLPromises);
  }
  public static assetURLToAppletURL(assetURL: AssetURL): Promise<AppletURL> {
    return new Promise<AppletURL>((resolve, reject): void => {
      GeoGebraService.calculateAppletSize(assetURL.downloadURL).subscribe((appletSize: Size): void => {
        resolve({
          id: assetURL.problem.id,
          URL: assetURL.downloadURL,
          width: appletSize.width,
          height: appletSize.height,
        });
      });
    });
  }

  public setDataService(dataService: DataService): void {
    this.dataService = dataService;
  }

  public send(destination: string, headers: object, message: ClassroomMessage) {
    this.webSocketService.send(destination, JSON.stringify(message));
  }

  public buildRecord(command: string, args: RecordArgs): MessageRecord {
    const whiteboardService: WhiteboardService = this.getWhiteboardService();
    return whiteboardService.buildRecord(command, args);
  }

  private static calculateAppletSize(ggbFilePath: string): Observable<Size> {
    return new Observable((observer: Subscriber<Size>): void => {

      const div: HTMLDivElement = document.createElement('div');
      div.id = 'tmp-applet';
      div.style.opacity = '0';
      div.style.position = 'absolute';
      div.style.overflow = 'hidden';
      document.body.appendChild(div);

      new GeogebraApplet({filename: ggbFilePath}, this.views, 'tmp-applet', () => {

        const rect: Rect = div.getBoundingClientRect();
        document.body.removeChild(div);

        observer.next(rect);
      });
    });
  }

  private postGGBApplet(rect: Rect, appletId: number, ggbFilePath: string, category: string, elementOwnerId?: string, elementIndex?: number): Promise<GeogebraApplet> {
    const whiteboard: Whiteboard = this.getWhiteboardByCategory(category);

    const appletForeignObject: ForeignObjectView = new ForeignObjectView(whiteboard.container, {overEvent: true, globalStyle: false}, rect, elementOwnerId, elementIndex);

    if (this.localUser.accessType === AccessType.STUDENT) {
      appletForeignObject.selectable = parseInt(appletForeignObject.ownerId) === this.localUser.id;
    }

    appletForeignObject.refPoint = appletForeignObject.center;
    const appletDivId: string = `applet-${appletId}-${Date.now()}`;

    appletForeignObject.setContent(`<div id="${appletDivId}"></div>`);
    appletForeignObject.SVG.setAttribute('data-class', 'ggb-applet');
    whiteboard.container.add(appletForeignObject);
    appletForeignObject.content.ondragstart = function(): boolean {return false;};

    appletForeignObject.SVG.setAttribute('data-applet-id', `${appletId}`);

    return new Promise<GeogebraApplet>((resolve, reject): void => {
      const geogebraApplet: GeogebraApplet = new GeogebraApplet({filename: ggbFilePath}, GeoGebraService.views, appletDivId, () => {
        resolve(geogebraApplet);
        this.geogebraStateUpdaterService.applets[appletId] = geogebraApplet;

        if (this.localUser.accessType === AccessType.STUDENT) {
          return;
        }

        geogebraApplet.registerListener('add', (label: string): void => {
          if (appletForeignObject.pointerEvents) { /* in edit mode */
            this.sendGGBAddEvent(appletId, label);
          }
        });

        geogebraApplet.registerListener('update', (): void => {
          if (appletId && this.geogebraStateUpdaterService.applets[appletId]) {
            this.geogebraStateUpdaterService.onAppletEdited(appletId);
          }
        });

        geogebraApplet.registerListener('client', (): void => {
          if (!appletId) {
            whiteboard.container.elements.forEach((elementView: ElementView): void => {
              if (elementView instanceof ForeignObjectView &&
                elementView.SVG.getAttribute('data-class') === 'ggb-applet' &&
                elementView.pointerEvents
              ) {
                appletId = parseInt(elementView.SVG.getAttribute('data-applet-id'));
              }
            });
          }

          if (appletId && this.geogebraStateUpdaterService.applets[appletId]) {
            this.geogebraStateUpdaterService.onAppletEdited(appletId);
          }
        });
      });
    });
  }

  private getWhiteboard(): Whiteboard {
    return this.classroom.privateTalkInfo.isPrivateWhiteboardOn && this.classroom.showPrivateWhiteboard ? this._privateWhiteboard : this._whiteboard;
  }

  private getWhiteboardService(): WhiteboardService {
    return this.classroom.privateTalkInfo.isPrivateWhiteboardOn && this.classroom.showPrivateWhiteboard ? this.privateWhiteboardService : this.whiteboardService;
  }

  private getWhiteboardByCategory(category: string): Whiteboard {
    switch (category) {
      case 'WHITEBOARD': {
        return this._whiteboard;
      }
      case 'PRIVATE_WHITEBOARD': {
        return this._privateWhiteboard;
      }
    }
  }

  private getWhiteboardServiceByCategory(category: string): WhiteboardService {
    switch (category) {
      case 'WHITEBOARD': {
        return this.whiteboardService;
      }
      case 'PRIVATE_WHITEBOARD': {
        return this.privateWhiteboardService;
      }
    }
  }

  private zoomAppletToFit(appletId: string, oldProps: any, size: Size, send: boolean = true): void {
    const applet: GeogebraApplet = this.geogebraStateUpdaterService.applets[appletId];
    if (!applet) {
      return;
    }

    const props: ViewPort | undefined = applet.getViewProperties();
    if (props) {
      /* use oldProps for yMin, because there is a bug in geogebra lib */
      const xMin: number = props.xMin;
      const yMin: number = oldProps.yMin;
      const xMax: number = props.xMin + (oldProps.width * oldProps.invXscale);
      const yMax: number = oldProps.yMin + (oldProps.height * oldProps.invYscale);
      console.log(xMin, yMin, xMax, yMax);

      applet?.runWithoutEventTriggering(() => {
        applet.setWidth(size.width);
        applet.setHeight(size.height);
        applet.setCoordSystem(xMin, xMax, yMin, yMax);
        if (send) {
          this.sendGGBResize(appletId, xMin, xMax, yMin, yMax, size);
        }
      });
    }
  }
}
